import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {api} from "@/shared/api";
import type {TeamInsightsRunResponse, TeamInsightsRunStatus, TeamInsightsUserResultResponse} from "@/shared/timr";
import type {Recommendation} from "./types";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function isTerminal(status: TeamInsightsRunStatus): boolean {
	return status === "DONE";
}

function parseRecommendations(results: TeamInsightsUserResultResponse[]): Recommendation[] {
	const recommendations: Recommendation[] = [];
	for (const result of results) {
		if (result.status !== "SUCCESS" || !result.recommendations) {
			continue;
		}
		for (const item of result.recommendations.items ?? []) {
			recommendations.push({
				resultId: result.resultId,
				userId: result.userId,
				userName: result.userName,
				item
			});
		}
	}
	return recommendations;
}

export interface TeamInsightsHook {
	run: TeamInsightsRunResponse | null;
	recommendations: Recommendation[];
	loading: boolean;
	error: boolean;
	recalculating: boolean;
	recalculateError: boolean;
	recalculateTimedOut: boolean;
	recalculate: () => void;
	submitFeedback: (resultId: number, itemId: string, positive: boolean) => Promise<boolean>;
}

export function useTeamInsights(): TeamInsightsHook {
	const [run, setRun] = useState<TeamInsightsRunResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [recalculating, setRecalculating] = useState(false);
	const [recalculateError, setRecalculateError] = useState(false);
	const [recalculateTimedOut, setRecalculateTimedOut] = useState(false);

	const pollTimeoutRef = useRef<number | undefined>(undefined);
	const activeRequestRef = useRef<JQuery.jqXHR | null>(null);
	const mountedRef = useRef(true);

	const clearPoll = useCallback(() => {
		if (pollTimeoutRef.current !== undefined) {
			window.clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = undefined;
		}
		activeRequestRef.current?.abort();
		activeRequestRef.current = null;
	}, []);

	const pollUntilDone = useCallback((deadline: number) => {
		const request = api("/teamInsights/latestRun.json").get();
		activeRequestRef.current = request;
		request
			.done((data) => {
				if (!mountedRef.current) {
					return;
				}
				if (data) {
					setRun(data);
				}
				if (data && !isTerminal(data.status)) {
					if (Date.now() >= deadline) {
						setRecalculateTimedOut(true);
						setRecalculating(false);
						return;
					}
					pollTimeoutRef.current = window.setTimeout(() => pollUntilDone(deadline), POLL_INTERVAL_MS);
				} else {
					setRecalculating(false);
				}
			})
			.fail((_jqXHR, textStatus) => {
				if (!mountedRef.current || textStatus === "abort") {
					return;
				}
				setRecalculateError(true);
				setRecalculating(false);
			});
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		const request = api("/teamInsights/latestRun.json")
			.get()
			.done((data) => {
				setRun(data ?? null);
				if (data && !isTerminal(data.status)) {
					setRecalculating(true);
					pollUntilDone(Date.now() + POLL_TIMEOUT_MS);
				}
			})
			.fail((_jqXHR, textStatus) => {
				if (textStatus !== "abort") {
					setError(true);
				}
			})
			.always(() => setLoading(false));

		return () => {
			mountedRef.current = false;
			request.abort();
			clearPoll();
		};
	}, [pollUntilDone, clearPoll]);

	const recalculate = useCallback(() => {
		clearPoll();
		setRecalculating(true);
		setRecalculateError(false);
		setRecalculateTimedOut(false);
		const request = api("/teamInsights/runs.json").post({});
		activeRequestRef.current = request;
		request
			.done((data) => {
				if (!mountedRef.current) {
					return;
				}
				setError(false);
				setRun(data);
				if (isTerminal(data.status)) {
					setRecalculating(false);
				} else {
					pollUntilDone(Date.now() + POLL_TIMEOUT_MS);
				}
			})
			.fail((_jqXHR, textStatus) => {
				if (!mountedRef.current || textStatus === "abort") {
					return;
				}
				setRecalculateError(true);
				setRecalculating(false);
			});
	}, [clearPoll, pollUntilDone]);

	const submitFeedback = useCallback((resultId: number, itemId: string, positive: boolean): Promise<boolean> => {
		return new Promise<boolean>(resolve => {
			api("/teamInsights/feedback.json")
				.post({resultId, itemId, positive})
				.done(() => resolve(true))
				.fail(() => resolve(false));
		});
	}, []);

	const recommendations = useMemo(() => (run ? parseRecommendations(run.results) : []), [run]);

	return {run, recommendations, loading, error, recalculating, recalculateError, recalculateTimedOut, recalculate, submitFeedback};
}
